import React, { useState, useEffect, useMemo } from 'react';

import { createColumnHelper } from '@tanstack/react-table';

import { noop } from '@adsk/alloy-react-helpers';

import Table, { TABLE_SIZES } from '../../sheets/components/Table';
import { applyBuiltInRules } from '../helpers/builtInRules';
import { getAllDisabledDocuments } from '../helpers/formatEntities';
import { getListViewCellRenderer } from '../helpers/getListViewCellRenderer';
import { processTreeViewEntities } from '../helpers/processTreeViewEntities';
import { isMalwareDetected } from '../helpers/utils';
import { useTranslation } from '../i18n';

const columnHelper = createColumnHelper();

const ListView = ({
  loading,
  error,
  multiple,
  disabledIds = [],
  selectedIds = [],
  entities,
  setSelection,
  onLoadMore,
  onTryAgain,
  dataViewProps = {},
}) => {
  const { t } = useTranslation();
  const {
    dataFilter,
    allowWholeFolderSelection = false,
    implicitlySelectedIds = [],
    setImplicitlySelectedIds = noop,
    rowIdsWithIndicator = [],
    renderIndicator = noop,
  } = dataViewProps;
  const data = useMemo(
    // Built-in rules run first so `dataFilter` sees them and can override any of it.
    () => processTreeViewEntities(applyBuiltInRules(entities, t), dataFilter),
    [dataFilter, entities, t],
  );

  const [allDisabledIds, setAllDisableIds] = useState(disabledIds);

  const getIdsToImplicitlySelect = React.useCallback(
    (latestSelectedIds) => {
      let idsToBeImplicitlySelected = [];

      data.forEach((entity) => {
        let ancestralIds;
        if ('folderType' in entity) {
          // Folder
          ancestralIds = (entity.parents ?? []).map((parent) => parent.urn);
        } else {
          // File
          ancestralIds = [
            entity.parentFolderUrn,
            ...(entity.parentFolder?.parents ?? []).map((parent) => parent.urn),
          ];
        }

        const isEntityImplicitlySelected = !!ancestralIds.find(
          (ancestralId) =>
            !!latestSelectedIds.find(
              (selectedId) => selectedId === ancestralId,
            ),
        );

        // Exclude malware files from implicit selection
        if (
          isEntityImplicitlySelected &&
          !isMalwareDetected(entity) &&
          !entity.disabled
        ) {
          idsToBeImplicitlySelected = [
            ...idsToBeImplicitlySelected,
            entity.urn,
          ];
        }
      });

      return idsToBeImplicitlySelected;
    },
    [data],
  );

  // Keep the implicitly-selected ids in sync with the current selection.
  //
  // Only push an update when the computed set actually differs from the
  // current one. `setImplicitlySelectedIds` is typically a `useControlled`
  // setter whose identity changes every time its value changes, so calling it
  // with a fresh array on every render would flip that identity, re-run this
  // effect, and spin into a "Maximum update depth exceeded" loop. The equality
  // guard lets the effect converge instead of feeding itself.
  useEffect(() => {
    const nextIds = allowWholeFolderSelection
      ? getIdsToImplicitlySelect(selectedIds)
      : [];

    const hasSameIds =
      nextIds.length === implicitlySelectedIds.length &&
      nextIds.every((id) => implicitlySelectedIds.includes(id));

    if (!hasSameIds) {
      setImplicitlySelectedIds(nextIds);
    }
  }, [
    selectedIds,
    allowWholeFolderSelection,
    getIdsToImplicitlySelect,
    implicitlySelectedIds,
    setImplicitlySelectedIds,
  ]);

  useEffect(() => {
    const retArray = getAllDisabledDocuments(data);

    // If no disabled documents were returned, use disableIds array as-is.
    // to avoid pointless renders.
    setAllDisableIds(
      retArray.length
        ? [...new Set([...disabledIds, ...retArray])]
        : disabledIds,
    );
  }, [disabledIds, data]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('file', {
        id: 'file',
        header: 'file',
        minSize: 200,
        enableResizing: false,
        cell: (props) => {
          const rowData = props.row.original;
          const showIndicator = rowIdsWithIndicator.find(
            (rowId) => rowId === rowData.urn,
          );

          return getListViewCellRenderer(
            rowData,
            [...allDisabledIds, ...implicitlySelectedIds],
            showIndicator && renderIndicator(),
          );
        },
      }),
    ],
    [
      rowIdsWithIndicator,
      allDisabledIds,
      implicitlySelectedIds,
      renderIndicator,
    ],
  );

  return (
    <Table
      hideHeader // Hide table header
      tableSize={TABLE_SIZES.LARGE}
      columns={columns}
      entities={data}
      disabledIds={[...allDisabledIds, ...implicitlySelectedIds]}
      selectedIds={[...selectedIds, ...implicitlySelectedIds]}
      multiple={multiple}
      onLoadMore={onLoadMore}
      loading={loading}
      error={error}
      onTryAgain={onTryAgain}
      setSelection={setSelection}
    />
  );
};

export default ListView;
